import React, { useState, useEffect, useCallback } from 'react';
import {
	Button,
	Input,
	Form,
	Spin,
	Card,
	Row,
	Col,
	Tooltip,
	Alert,
	Tabs,
	Select,
	notification,
} from 'antd';
import { retrieveCompletion } from '../../utils/api.js';
import TokenTextArea from './TokenTextArea.jsx';
import InstructionSelect from './InstructionSelect.jsx';
import propTypes from 'prop-types';
import ModelSelect from './ModelSelect.jsx';
import './CompletionTool.css';

const { TabPane } = Tabs;

const SUMMARY_LENGTHS = [
	{
		id: 'mini',
		label: 'Mini',
		value: 125,
	},
	{
		id: 'short',
		label: 'Kort',
		value: 250,
	},
	{
		id: 'medium',
		label: 'Mellan',
		value: 500,
	},
	{
		id: 'long',
		label: 'Lång',
		value: 1000,
	},
	{
		id: 'xl',
		label: 'XL',
		value: 2000,
	},
	{
		id: 'xxl',
		label: 'XXL',
		value: 3000,
	},
	{
		id: 'xxxl',
		label: 'XXXL',
		value: 4000,
	},
];
function CompletionTool({ prefill, config, onChange }) {
	const [text, setText] = useState('');
	const [instructions, setInstructions] = useState([]);
	const [selectedLength, setSelectedLength] = useState(500);
	const [selectedModel, setSelectedModel] = useState(null);
	const [resultLoading, setResultLoading] = useState(false);
	const [results, setResults] = useState([]);
	const [errors, setErrors] = useState([]);

	const handleError = (message) => {
		notification.error({
			message: message,
		});
		setErrors([
			{
				id: new Date().getTime(),
				message,
			},
			...errors,
		]);
	};

	const longestInstruction = () => {
		if (!instructions || instructions.length === 0) {
			return '';
		}

		let longest = instructions[0].instruction;
		instructions.forEach((instruction) => {
			if (instruction.instruction.length > longest.length) {
				longest = instruction.instruction;
			}
		});

		return longest;
	};

	const requestCompletion = async () => {
		if (!selectedModel) {
			handleError('Inga modeller tillgängliga.');
			return;
		}

		if (!instructions || instructions.length === 0) {
			handleError('Inga instruktioner valda.');
			return;
		}

		if (!text || text.length < 50) {
			handleError('Text är för kort. Minst 50 tecken krävs.');
			return;
		}

		setResultLoading(true);

		for (const instruction of instructions) {
			try {
				const response = await retrieveCompletion(
					text,
					selectedModel.id,
					selectedLength,
					instruction.instruction
				);

				if (response.status === 200) {
					setResults((prevResults) => [
						...prevResults,
						{
							instruction: instruction,
							output: response.message,
						},
					]);
				} else {
					let error = 'Något gick fel';
					if (response.error && response.error.description) {
						error = response.error.description;
					} else if (response.status >= 400 && response.description) {
						error = response.description;
					}
					handleError(error);
				}
			} catch (error) {
				handleError('Något gick fel', error.message);
			}
		}

		setResultLoading(false);
	};

	const handleEdit = (targetKey, action) => {
		if (action === 'remove') {
			setResults(results.filter((result, index) => index !== parseInt(targetKey)));
		}
	};

	return (
		<Row gutter={24}>
			<Col xl={24} xxl={4} style={{ marginBottom: 8 }}>
				<Card bodyStyle={{ padding: 12 }}>
					<Col xl={6} xxl={24}>
						<ModelSelect onChange={(model) => setSelectedModel(model)} defaultModel='gpt-4o' />
					</Col>
					<Col xl={4} xxl={24}>
						<Form.Item label='Max längd'>
							<Select
								size='default'
								style={{ width: '100%' }}
								defaultActiveFirstOption={false}
								defaultValue={SUMMARY_LENGTHS[2].value}
								dropdownMatchSelectWidth
								onChange={(value) => setSelectedLength(value)}
							>
								{SUMMARY_LENGTHS.map((lengthOption) => (
									<Select.Option key={lengthOption.id} value={lengthOption.value}>
										{lengthOption.label} ({lengthOption.value} tokens)
									</Select.Option>
								))}
							</Select>
						</Form.Item>
					</Col>
					<Col xl={14} xxl={24}>
						<InstructionSelect
							multiple
							type='SUMMARY'
							onChange={(instructions) => setInstructions(instructions)}
						/>
					</Col>
				</Card>
			</Col>

			<Col xl={24} xxl={20}>
				<Row gutter={24}>
					<Col lg={24} xl={12}>
						<Form.Item>
							<TokenTextArea
								label={<h3 style={{ lineHeight: 'normal' }}>Text</h3>}
								inputs={prefill}
								autosize={{ minRows: 15, maxRows: 25 }}
								instruction={longestInstruction()}
								onChange={(value) => setText(value)}
								model={selectedModel}
								responseLength={selectedLength}
								onError={(error) => handleError(error)}
							/>
							<Button
								style={{ float: 'right', width: 110 }}
								onClick={() => requestCompletion()}
								type='primary'
								disabled={resultLoading}
							>
								{resultLoading ? <Spin size='small' /> : 'Generera'}
							</Button>
						</Form.Item>
					</Col>
					<Col lg={24} xl={12}>
						<h3 style={{ lineHeight: 'normal' }}>Resultat</h3>
						<Spin spinning={resultLoading && !results.length}>
							<div className='card-container' style={{ minHeight: 150 }}>
								{results.length > 0 && (
									<Tabs type='editable-card' hideAdd onEdit={handleEdit}>
										{results.map((result, index) => (
											<TabPane
												tab={
													<Tooltip title={result.instruction.instruction} placement='topRight'>
														{result.instruction.name}
													</Tooltip>
												}
												key={index}
												style={{ background: '#FFF' }}
												closable
											>
												<Row>
													<Col span={20}></Col>
													<Col span={4}>
														<Tooltip placement='top' trigger='click' title={'Kopierad!'}>
															<Button
																size={'small'}
																type='default'
																onClick={() => navigator.clipboard.writeText(result.output)}
																style={{ float: 'right' }}
															>
																Kopiera
															</Button>
														</Tooltip>
													</Col>
												</Row>
												<Input.TextArea
													style={null}
													autosize={{ minRows: 12, maxRows: 23 }}
													value={result.output}
													readOnly
													disabled={!(result.output && result.output.length)}
												/>
											</TabPane>
										))}
									</Tabs>
								)}
							</div>
						</Spin>
					</Col>
				</Row>
			</Col>
		</Row>
	);
}

CompletionTool.defaultProps = {
	prefill: [],
	onChange: () => {},
};

CompletionTool.propTypes = {
	prefill: propTypes.arrayOf(propTypes.object),
	onChange: propTypes.func,
};

export default CompletionTool;



// WEBPACK FOOTER //
// ./src/components/ai/CompletionTool.jsx